import React, { useContext } from 'react';
import {
    FieldProps,
    BaseFieldDisplayProps,
    BaseFieldDisplay,
    ToggleEditViewRendererProps,
    LabelRenderer,
} from 'features/account-settings/components/SettingsTab/PersonalSettings/utils';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { EditableFieldsContext } from '../../context';
import { useAlert } from 'modules/alerts';
import {
    AchVerificationStatus,
    isVerified,
    verficationExpiredOrFailed,
} from 'common/api/e-comm/ach/models/AchAccount';
import { useLocation } from 'react-router-dom';
import { PaymentOptionsContext } from './context';
import { ReactComponent as NotificationBell } from '../../../../../../media/images/notif-bell.svg';
import { AddBankAccountPromptSection } from './components/AddBankAccountPromptSection';
import { AutomatedVerificationFlow } from './components/AutomatedVerificationFlow';
import { MicroTransactionsFlow } from './components/MicroTransactionsFlow';
import { OAuthFlow } from './components/OAuthFlow';
import { VerificationExpiredFlow } from './components/VerificationExpiredFlow';
import { useAchControls } from './components/useAchControls';

interface BankFieldProps extends FieldProps {}

interface BankFieldDisplayProps extends Omit<BaseFieldDisplayProps, 'onClick'> {}

const LabelRendererWithNotificationBell = ({ label }: { label: React.ReactNode }) => {
    const ach = useRootSelector((state: RootState) => state.store.account.achAccount);
    const verificationHasExpiredOrFailed = ach && verficationExpiredOrFailed(ach);

    return (
        <div className="flex gap-1">
            <LabelRenderer label={label} />
            {verificationHasExpiredOrFailed && <NotificationBell width={18} height={18} />}
        </div>
    );
};

const ToggleEditViewRenderer = ({ isEditing, onClick }: ToggleEditViewRendererProps) => {
    const ach = useRootSelector((state: RootState) => state.store.account.achAccount);
    const { addresses, createMode } = useContext(PaymentOptionsContext);

    const verificationHasExpiredOrFailed = ach && verficationExpiredOrFailed(ach);
    if (verificationHasExpiredOrFailed) {
        // CASE: if a user's verification has expired or failed
        // we don't want to render any buttons
        return null;
    }

    const hasAchAccount = ach !== null;
    const userHasAddresses = addresses.length > 0;

    const isPendingVerification = ach && !isVerified(ach);
    const btnText = isPendingVerification
        ? 'Cancel Bank Account Setup'
        : 'Delete this bank account';

    return (
        <p onClick={onClick} className="font-semibold underline text-brand text-xs cursor-pointer">
            {!createMode && hasAchAccount && !ach?.isOnHold && !ach?.isLocked ? btnText : null}
            {!createMode && !hasAchAccount && (isEditing || !userHasAddresses) ? 'Cancel' : null}
        </p>
    );
};

const BankFieldDisplay = (props: BankFieldDisplayProps) => {
    const { setFieldToEdit } = useContext(EditableFieldsContext);
    const ach = useRootSelector((state: RootState) => state.store.account.achAccount);
    const { setSelectedAddress } = useContext(PaymentOptionsContext);
    const hasBankLinked = Boolean(ach);
    const alert = useAlert();

    const { deleteAch } = useAchControls();

    const onClick = () => {
        // Case: We have a bank account -- user is pressing `delete bank account`
        if (hasBankLinked) {
            const isPendingVerification = ach && !isVerified(ach);

            const lastFour = ach?.accountNumber?.replace(/[x|-]+/g, '');

            const title = (
                <p className="text-error text-2xl">
                    {isPendingVerification ? 'Cancel Bank Account Setup?' : 'Delete Bank Account'}
                </p>
            );

            const message = isPendingVerification
                ? 'If you do not complete setup, any stored information will be cleared. \n\n Are you sure you want to cancel?'
                : `Are you sure you want to delete bank account ending in ${lastFour}`;

            alert({
                title,
                message,
                buttons: [
                    {
                        text: 'Yes',
                        onClick: deleteAch,
                    },
                    {
                        text: 'Cancel',
                        className: 'underline',
                    },
                ],
            });
        }
        // Case: User is editing is pressing `cancel` should turn off edit-mode
        // and unselect addresses if any were chosen
        else if (props.isEditing) {
            setFieldToEdit('');
            setSelectedAddress('');
        }
    };

    return (
        <BaseFieldDisplay
            {...props}
            renderers={{
                ToggleEditViewRenderer,
                LabelRenderer: LabelRendererWithNotificationBell,
            }}
            onClick={onClick}
        />
    );
};

export const BankField = ({ id, isEditing }: BankFieldProps) => {
    const ach = useRootSelector((state: RootState) => state.store.account.achAccount);
    const params = new URLSearchParams(useLocation().search);
    const isOAuth = Boolean(params.get('oauth_state_id'));

    const renderDisplayField = () => {
        // CASE: Handle the different views that need to render depending on the
        // ACH account status(not to be confused with verificationStatus).
        // The `status` encompasses:
        //
        // - verificationStatus :: all of it's possible states
        // - isLocked
        // - isOnHold
        // - isOAuth            :: when user is connecting through plaid via oauth flow
        if (
            Boolean(ach) &&
            ach?.verificationStatus === AchVerificationStatus.PendingManualVerification
        ) {
            return <MicroTransactionsFlow />;
        } else if (
            Boolean(ach) &&
            ach?.verificationStatus === AchVerificationStatus.PendingAutomatedVerification
        ) {
            // CASE: Verification status is AutomatedVerification
            return <AutomatedVerificationFlow />;
        } else if (isOAuth) {
            // CASE: we're in OAUTH flow
            return <OAuthFlow />;
        } else if (!ach) {
            // CASE: User has no ACH account we should prompt them to add one
            return <AddBankAccountPromptSection id={id} />;
        } else if (ach && ach?.isLocked) {
            return (
                <div>
                    <p className="text-sm text-grey-1">
                        Your online payment account has been locked.
                    </p>
                    <p className="text-sm text-grey-1">
                        Please ask your coordinator to call the Customer Support team at
                        1-800-727-4715, option 3, to unlock your account.
                    </p>
                </div>
            );
        }
        if (
            ach?.verificationStatus === AchVerificationStatus.VerificationExpired ||
            ach?.verificationStatus === AchVerificationStatus.VerificationFailed
        ) {
            return <VerificationExpiredFlow id={id} />;
        } else if (ach && ach?.isOnHold) {
            return (
                <p className="text-sm text-grey-1">
                    Thank you for enrolling in Online ACH Payments. We need a few days to finalize
                    your account, so please check back in 1-2 business days
                </p>
            );
        }

        // CASE: user has an ach account -- we should just show it
        return <span data-recording-sensitive>{ach?.accountNumber}</span>;
    };

    return (
        <BankFieldDisplay id={id} label="Bank Account" isEditing={isEditing}>
            {renderDisplayField()}
        </BankFieldDisplay>
    );
};
